import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { GpuFormFieldAppearance } from '@geneplanet/ngx-ui';
import {
  IDateObject,
  Marker,
} from 'src/app/analyses/interfaces/analyses.types';
import {
  objectToDate,
  subtractCurrentDateYear,
} from 'src/app/shared/util/date-util';
import { BloodAddBase } from '../../interfaces/blood-add-base.interface';
import { BloodConfigurationChoice } from '../../interfaces/blood-configuration.interface';
import { BloodResultChoice } from '../../interfaces/blood-analysis-result.interface';
import { AnalyticsService, AnalyticsValue } from '@geneplanet/ngx-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ngbDateFromDate,
  ngbDateFromISO,
  ngbDateToISO,
} from 'src/app/shared/util/date-util';
import { Subject } from 'rxjs';
import { BloodPost } from '../../interfaces/blood-post.inteface';

@UntilDestroy()
@Component({
  selector: 'app-blood-add-choice',
  templateUrl: './blood-add-choice.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BloodAddChoiceComponent implements BloodAddBase {
  public form = this.fb.group({
    value: [<number>null, Validators.required],
    date: [ngbDateFromDate(new Date()), Validators.required],
  });
  public analysisResult: BloodResultChoice;
  public showDate = true;
  public configuration: BloodConfigurationChoice;
  public formFieldAppearance: GpuFormFieldAppearance;
  public submitted = new Subject<void>();

  protected now = ngbDateFromDate(new Date());
  protected minDate = ngbDateFromDate(subtractCurrentDateYear(10));

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    this.trackForm();
  }

  public init(): void {
    if (this.analysisResult) {
      this.form.patchValue({
        value: this.analysisResult.result.markers[0].value,
        date: ngbDateFromISO(this.analysisResult.result.generated),
      });
      this.cdRef.markForCheck();
    }
  }

  public onSubmit() {
    if (this.form.invalid) {
      return null;
    }

    const configMarker = this.configuration.markers[0];

    const descriptive = configMarker.qualitativeValueWeights.find(
      (weight) => weight.value === this.form.value.value
    ).key;

    const marker: Marker = {
      markerId: configMarker.id,
      unitId: configMarker.units[0].id,
      value: Number(this.form.value.value),
      descriptive,
    };

    const generatedDate = objectToDate(
      (this.form.value.date as Partial<IDateObject>) ?? {}
    ).toISOString();

    const payload: BloodPost = {
      generated: generatedDate,
      markers: [marker],
    };
    return payload;
  }

  private trackForm() {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((value: any) => {
          const valueObj: AnalyticsValue = {
            isValid: this.form.controls[key].valid,
          };

          if (key === 'value') {
            valueObj['value'] = String(value);
          } else {
            valueObj['value'] = ngbDateToISO(value);
          }

          this.analyticsService.trackFormValue(
            this.analysisResult ? 'analyses-edit' : 'analyses-add',
            key,
            valueObj,
            this.configuration.id,
            this.configuration.markers[0].id
          );
        });
    });
  }
}
